import LikeDislikeTooltip from './LikeDislikeTooltip'
import { Box, IconButton, Typography } from '@mui/material'
import { ChatBubble, ThumbDown, ThumbUp, Visibility, VisibilityOff } from '@mui/icons-material'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import { useVoteMutation, VoteDTO } from '../../store/redux/reducers/voteSlice'
import VoteType from '../../store/types/enums/VoteType'
import TargetType from '../../store/types/enums/TargetType'
import { createErrorMessage } from '../../store/utils'
import { t } from 'i18next'
import { useNotification } from '../../store/redux/hooks'
import Voter from '../../store/types/models/Voter'
import BasicTooltip from '../Tooltip/BasicTooltip'
import { useUpdateCandidateFollowingMutation } from '../../store/redux/reducers/candidateSlice'

type LikeDislikeSectionProps = {
  targetId: string
  targetType: TargetType
  upVoters: Voter[] | undefined
  downVoters: Voter[] | undefined
  big?: boolean
  handleCommentClick?: () => void
  withoutComments?: boolean
  commentsCount?: number
  isFollowingCandidate?: boolean
}

const LikeDislikeCommentSection = ({
  targetId,
  targetType,
  upVoters,
  downVoters,
  big,
  handleCommentClick,
  withoutComments,
  commentsCount,
  isFollowingCandidate,
}: LikeDislikeSectionProps) => {
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)

  const ifLiked = useMemo(() => {
    return upVoters?.some(user => user.id === currentUser?.id)
  }, [upVoters, currentUser?.id])
  const ifDisliked = useMemo(() => {
    return downVoters?.some(user => user.id === currentUser?.id)
  }, [downVoters, currentUser?.id])

  const [vote] = useVoteMutation()
  const [updateFollowing] = useUpdateCandidateFollowingMutation()

  const handleVote = async (voteType: VoteType) => {
    const voteBody: VoteDTO = { voteType, targetId, targetType }
    try {
      await vote(voteBody).unwrap()
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('message.likeDislikeError')} ${errorDescription}`,
        type: 'error',
      })
    }
  }

  const handleLikeClick = () => {
    if (!currentUser) {
      return
    }
    handleVote(VoteType.UP)
  }

  const handleDislikeClick = () => {
    if (!currentUser) {
      return
    }
    handleVote(VoteType.DOWN)
  }

  const handleUpdateFollowing = async () => {
    await updateFollowing({ candidateId: targetId, body: { following: Boolean(!isFollowingCandidate) } }).unwrap()
  }

  const size = big ? 24 : 16
  const color = big ? 'secondary.darkHover' : 'secondary.dark'
  const fontWeight = big ? 'bold' : 'normal'

  return (
    <>
      <LikeDislikeTooltip users={upVoters || []}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
          <ThumbUp
            sx={{ fontSize: size, color: ifLiked ? 'primary.main' : color, cursor: 'pointer' }}
            onClick={handleLikeClick}
          />
          <Typography fontWeight={fontWeight}>{upVoters?.length}</Typography>
        </Box>
      </LikeDislikeTooltip>
      <LikeDislikeTooltip users={downVoters || []}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
          <ThumbDown
            sx={{
              fontSize: size,
              color: ifDisliked ? 'primary.main' : color,
              cursor: 'pointer',
            }}
            onClick={handleDislikeClick}
          />
          <Typography fontWeight={fontWeight}>{downVoters?.length}</Typography>
        </Box>
      </LikeDislikeTooltip>
      {!withoutComments && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ChatBubble sx={{ fontSize: size, color: color, cursor: 'pointer' }} onClick={handleCommentClick} />
          <Typography fontWeight={fontWeight}>{commentsCount || 0}</Typography>
        </Box>
      )}

      {!withoutComments && (
        <BasicTooltip
          placement={'bottom'}
          title={
            <Typography>
              {isFollowingCandidate
                ? t('page.name.candidates.turnOffCandidateNotifications')
                : t('page.name.candidates.turnOnCandidateNotifications')}
            </Typography>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1, ml: 1 }}>
            <IconButton onClick={handleUpdateFollowing} sx={{ color: color }}>
              {isFollowingCandidate ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Box>
        </BasicTooltip>
      )}
    </>
  )
}

export default LikeDislikeCommentSection
